import { useCallback, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { LoginContext, useToast } from '@aslanpay/portal-common-lib';

import useAuthContext from 'app/hooks/useAuthContext';
import useCreateLegalAcceptance from 'app/hooks/useCreateLegalAcceptance';

import { useAuthenticationStore } from 'app/store';

/**
 * Completes an employee sign-up once Cognito authentication has succeeded: records terms acceptance
 * (mirroring the mobile app), lands the user in the given portal context, and clears the sign-up
 * state so an abandoned attempt can restart cleanly.
 */
const useFinishSignUp = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { completeLoginContextAuthentication } = useAuthContext();
  const resetAuthenticationData = useAuthenticationStore((state) => state.resetAuthenticationData);

  const contextRef = useRef<LoginContext>('EMPLOYEE');

  const finalise = useCallback(() => {
    completeLoginContextAuthentication(contextRef.current);
    queryClient.invalidateQueries({ queryKey: ['getAuthenticatedUser'] });
    // Refresh the individual so a just-activated employee is seen as ACTIVE and any blocking
    // activation step stops rendering.
    queryClient.invalidateQueries({ queryKey: ['fetchIndividual'] });
    resetAuthenticationData();
  }, [completeLoginContextAuthentication, queryClient, resetAuthenticationData]);

  const { mutate: recordAcceptance } = useCreateLegalAcceptance({
    onSuccess: finalise,
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'We could not record your acceptance of the terms. Please contact support.',
      });
      // Still land the user — a failed acceptance record must not strand a signed-in employee.
      finalise();
    },
  });

  return useCallback(
    (context: LoginContext) => {
      contextRef.current = context;
      recordAcceptance({
        acceptanceFlag: true,
        acceptanceTimestamp: new Date().toISOString(),
        document: 'GENERAL_APP_USAGE_TERMS_AND_CONDITIONS',
      });
    },
    [recordAcceptance]
  );
};

export default useFinishSignUp;
